import {
  unityContentsService,
  unityProfileService,
} from '@tbx/experience-widgets-lib';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import * as actions from './actions';
import { CONTENT_TYPES, types } from './constants';
import * as selectors from './selectors';

function* fetchContentWidget(action) {
  const { accessToken, contentID } = action;

  try {
    const { result } = yield call(
      unityContentsService.getContentsById,
      accessToken,
      contentID,
    );

    if (!result || result.error) {
      throw new Error(result.error);
    }

    yield put(actions.loadWidgetSuccess(result));
  } catch (e) {
    console.error('CONTENTOVERVIEW_SECTION_WIDGET_FETCH_ERROR: ', e);
    yield put(actions.loadWidgetError(e));
  }
}

function* fetchContentEpisodesAndContentRelatedWidget(action) {
  const {
    content,
    page: incomingPage,
    pageSize = 20,
    season: incomingSeason,
    isSeasonChange = false,
  } = action;

  const isSeries = content.contentType?.toUpperCase() === 'SERIE';
  let targetSeason = null;

  if (isSeries) {
    const seasons = content.seasons || [];
    let seasonToUse = incomingSeason; // Empezar con la que viene

    // Si es un cambio de temporada explícito, asegurarnos de usar la season que viene
    // Si no es cambio de temporada, o no viene season, aplicar defaults
    if (!isSeasonChange) {
      if (
        seasonToUse === undefined ||
        seasonToUse === null ||
        !seasons.includes(seasonToUse)
      ) {
        if (seasons.length > 0) {
          seasonToUse = seasons[0];
        } else {
          seasonToUse = null; // No hay temporadas válidas
        }
      }
    } else if (seasonToUse === undefined || seasonToUse === null) {
      // ESTE BLOQUE SE EJECUTA SI:
      // 1. isSeasonChange === true (porque estamos en el 'else' del 'if (!isSeasonChange)')
      // 2. incomingSeason era undefined o null
      if (seasons.length > 0) {
        // Si hay temporadas disponibles...
        console.warn(
          'Season change indicated but no valid season provided, defaulting to first season.',
        );
        // Ya está asignando la primera temporada
        seasonToUse = seasons[0];
      } else {
        // Si NO hay temporadas disponibles...
        console.error(
          'Season change indicated but no valid season provided and no seasons exist.',
        );
        // Se asigna null porque no hay nada que cargar
        seasonToUse = null;
      }
    }

    if (seasonToUse !== null) {
      targetSeason = Number(seasonToUse);
    }
  }

  // --- 2. Determinar la Página Real a Usar ---
  let pageToUse = incomingPage === undefined ? 1 : Number(incomingPage); // Default 1

  // *** USAR EL FLAG DIRECTAMENTE ***
  if (isSeasonChange) {
    console.log(
      `[Saga] Flag 'isSeasonChange' es true. Forzando page = 1 (incomingPage era ${incomingPage}).`,
    );
    pageToUse = 1;
  } else {
    console.log(
      `[Saga] Flag 'isSeasonChange' es false. Usando page = ${pageToUse}.`,
    );
  }

  try {
    // Verificar si ya tenemos todos los episodios de la temporada actual
    if (!isSeasonChange && pageToUse > 1) {
      const currentEpisodes = yield select((state) =>
        selectors.selectContentEpisodesWidget(state),
      );

      const totalEpisodesInState = yield select((state) =>
        selectors.selectTotalEpisodes(state),
      );

      // Si ya tenemos todos los episodios, no hacemos el llamado a la API
      if (currentEpisodes.length >= totalEpisodesInState) {
        console.log(
          `[Saga] Ya tenemos todos los episodios (${currentEpisodes.length}/${totalEpisodesInState}). Cancelando llamada a API.`,
        );
        return;
      }

      // Verificar si la próxima página tendrá episodios
      const episodesPerPage = pageSize;
      const currentPage = pageToUse - 1; // La página anterior
      const episodesAlreadyLoaded = currentPage * episodesPerPage;

      if (episodesAlreadyLoaded >= totalEpisodesInState) {
        console.log(
          `[Saga] No hay más episodios para cargar. Ya se cargaron ${episodesAlreadyLoaded} de ${totalEpisodesInState}. Cancelando llamada a API.`,
        );
        return;
      }
    }

    const currentToken = yield select((state) =>
      selectors.selectAccessToken(state),
    );

    const contentTypes = content.contentType?.toUpperCase() ?? '';
    let dataResult;

    // --- 3. Realizar la Llamada a la API (usando pageToUse y targetSeason) ---
    if (isSeries) {
      if (targetSeason !== null) {
        console.log(
          `[Saga] Calling API for Serie ${content.id}, Season ${targetSeason}, Page ${pageToUse}`,
        );
        dataResult = yield call(
          CONTENT_TYPES[content.contentType].SERVICE,
          currentToken,
          content.id,
          {
            contentType: contentTypes,
            page: pageToUse,
            pageSize,
            season: targetSeason,
          },
        );
      } else {
        console.warn(
          `[Saga] Serie ${content.id} - No target season determined. Skipping API call.`,
        );
        dataResult = { result: { result: [], total: 0 } };
      }
    } else {
      console.log(`[Saga] Calling API for ${contentTypes} ${content.id}`);
      dataResult = yield call(
        CONTENT_TYPES[content.contentType].SERVICE,
        currentToken,
        content.id,
        { contentType: contentTypes },
      );
    }

    if (!dataResult || dataResult.error) {
      throw new Error(dataResult.error || 'Error desconocido en la API');
    }

    const episodes =
      dataResult?.result?.result !== null ? dataResult?.result?.result : [];
    const totalEpisodes =
      dataResult?.result?.total !== null ? dataResult?.result?.total : 0;

    const currentEpisodes = yield select((state) =>
      selectors.selectContentEpisodesWidget(state),
    );

    const mergedEpisodes =
      pageToUse === 1 ? episodes : [...currentEpisodes, ...episodes];

    yield put(actions.totalEpisodes(totalEpisodes));

    yield put(
      actions[CONTENT_TYPES[content.contentType].LOAD_ACTION](mergedEpisodes),
    );
  } catch (e) {
    console.error('CONTENTEPISODES_WIDGET_FETCH_ERROR: ', e);
    yield put(actions.loadWidgetError(e));
  }
}

function* fetchEpisodeCurrentContentEpisodeByProfile(action) {
  const { accessToken, contentID, profileID } = action;

  try {
    const { result } = yield call(
      unityProfileService.getContentByProfile,
      accessToken,
      contentID,
      profileID,
    );

    if (!result || result.error) {
      throw new Error(result.error);
    }

    yield put(actions.getEpisodeCurrentContentByProfileSuccess(result));
  } catch (e) {
    console.error(types.EPISODE_CURRENT_CONTENT_PROFILE_FETCH_FAILURE, e);
    yield put(actions.getEpisodeCurrentContentByProfileError(e));
  }
}

function* fetchContensUrlById(action) {
  const { accessToken, contentID } = action;

  try {
    const { result } = yield call(
      unityContentsService.getContentsUrlById,
      accessToken,
      contentID,
      {},
    );

    if (!result || result.error) {
      throw new Error(result.error);
    }

    yield put(actions.getContentsUrlByIdSuccess(result));
  } catch (e) {
    console.error('CONTENTOVERVIEW_SECTION_CONTENTS_URL_FETCH_ERROR: ', e);
    yield put(actions.getContentsUrlByIdError(e));
  }
}

function* fetchContentAccess(action) {
  const { accessToken, contentID } = action;
  try {
    const { result } = yield call(
      unityContentsService.getContentAccess,
      accessToken,
      contentID,
      {},
    );

    if (!result || result.error) {
      throw new Error(result.error);
    }

    yield put(actions.getContentsAccessSuccess(result));
  } catch (e) {
    console.error('CONTENTOVERVIEW_SECTION_ACCESS_FETCH_ERROR: ', e);
    yield put(actions.getContentsAccessError(e));
  }
}

function* saga() {
  yield takeLatest(types.WIDGET_FETCH, fetchContentWidget);
  yield takeLatest(
    types.WIDGET_FETCH_SUCCESS,
    fetchContentEpisodesAndContentRelatedWidget,
  );
  yield takeLatest(
    types.EPISODE_CURRENT_CONTENT_PROFILE_FETCH,
    fetchEpisodeCurrentContentEpisodeByProfile,
  );
  yield takeLatest(types.CONTENTS_URL_ID_FETCH, fetchContensUrlById);
  yield takeLatest(types.CONTENTS_ACCESSS_FETCH, fetchContentAccess);
}

export default saga;
